import type {
  DecisionState,
  PublicInvoiceSummary,
  SharedComment,
  SharedProtectionPlan,
  SharedQuoteLine,
} from '@/api/quote'

/** How a customer refers to a row: "2: Fryer" for numbered items, the description for options. */
export function lineLabel(
  line: Pick<SharedQuoteLine, 'parent_row_ref' | 'display_number' | 'description'>,
) {
  return line.parent_row_ref || !line.display_number
    ? line.description
    : `${line.display_number}: ${line.description}`
}

/** A line as the quote names it: manufacturer and SKU, or the description when it has neither. */
export function lineTitle(line: Pick<SharedQuoteLine, 'manufacturer' | 'model' | 'description'>) {
  return [line.manufacturer, line.model].filter(Boolean).join(' ') || line.description
}

/** The item a comment is on, named as the quote names it; the saved label once the item is gone. */
export function commentTag(comment: SharedComment, lines: SharedQuoteLine[]) {
  const line = lines.find((item) => item.row_ref === comment.row_ref)
  return line ? lineLabel({ ...line, description: lineTitle(line) }) : comment.row_label
}

// Photos and files on a comment (USA-1345): the storefront chat's types and limits, checked again by the API.
export const MAX_FILES = 5
const MAX_FILE_BYTES = 10 * 1024 * 1024
const FILE_EXTENSIONS = ['csv', 'doc', 'docx', 'gif', 'jpeg', 'jpg', 'pdf', 'png', 'txt', 'webp', 'xls', 'xlsx']
export const FILE_ACCEPT = FILE_EXTENSIONS.map((extension) => `.${extension}`).join(',')

/** Why these files can't go with a comment, or null when they can. */
export function fileProblem(files: Pick<File, 'name' | 'size'>[]) {
  if (files.length > MAX_FILES) return `Send up to ${MAX_FILES} files at a time.`
  for (const file of files) {
    if (!FILE_EXTENSIONS.includes(file.name.split('.').at(-1)?.toLowerCase() ?? ''))
      return 'Send photos (JPG, PNG, GIF, WebP), PDFs, Excel, CSV, Word or text files.'
    if (!file.size) return `${file.name} is empty.`
    if (file.size > MAX_FILE_BYTES) return 'Files can be up to 10 MB.'
  }
  return null
}

/** `912 KB`, `1.4 MB`. */
export function fileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const NAME_KEY = 'usars-quote-name'

/** The name this browser last commented under, so the customer types it once. */
export function rememberedName() {
  try {
    return window.localStorage.getItem(NAME_KEY) ?? ''
  } catch {
    return ''
  }
}

export function rememberName(name: string) {
  try {
    if (name) window.localStorage.setItem(NAME_KEY, name)
  } catch {
    // Private browsing: the name is asked again next time.
  }
}

/**
 * An open invoice with money left to pay (and no bank payment clearing) on a final quote the
 * customer has not declined or sent back for changes. Paying and signing happen in either order (USA-1285).
 */
export function invoicePayable(invoice: PublicInvoiceSummary, decision: DecisionState) {
  return (
    invoice.status === 'open' &&
    Number(invoice.amount_remaining) > 0 &&
    !invoice.payment_processing &&
    (decision === 'pending' || decision === 'approved')
  )
}

/** `24` → "2 years", `18` → "18 months". */
function coverage(months: number) {
  if (months % 12) return `${months} months`
  return months === 12 ? '1 year' : `${months / 12} years`
}

/**
 * How a protection plan reads to the customer (USA-1162): the coverage it adds first and the total
 * after, so it never reads as buying the combined term. The quote PDF says the same.
 */
export function protectionPlanText(plan: SharedProtectionPlan) {
  const warranty = plan.manufacturer_warranty_months
  const length = warranty ? (warranty % 12 ? `${warranty}-month ` : `${warranty / 12}-year `) : ''
  const covers = plan.covers ? ` on the ${plan.covers}` : ''
  return [
    `Adds ${coverage(plan.months_added)} of coverage beyond the manufacturer's ${length}warranty${covers}.`,
    `Covered for ${coverage(plan.term_months)} in total from purchase.`,
  ]
}
